/**
 * Pure builders for the persistent `users/{uid}` profile document.
 *
 * The profile deliberately holds only durable display data. OAuth access
 * tokens, refresh tokens and API keys are never written here: YouTube
 * authorization lives in memory for the lifetime of the tab only.
 */
import { ACCOUNT_TYPE, authDisplayName, deriveAccountType, isGenericDisplayName } from './authIdentity';

/** Any key outside this list is dropped before a write. */
export const PROFILE_ALLOWED_FIELDS = [
  'uid',
  'displayName',
  'photoURL',
  'accountType',
  'createdAt',
  'updatedAt',
];

/** Keys that must never reach Firestore, even if a caller passes them. */
export const PROFILE_FORBIDDEN_FIELDS = [
  'accessToken',
  'refreshToken',
  'idToken',
  'apiKey',
  'credential',
  'oauthAccessToken',
  'oauthRefreshToken',
  'stsTokenManager',
  'providerData',
];

export function sanitizeProfilePayload(payload) {
  if (!payload || typeof payload !== 'object') return {};
  return PROFILE_ALLOWED_FIELDS.reduce((accumulator, key) => {
    if (payload[key] !== undefined) {
      accumulator[key] = payload[key];
    }
    return accumulator;
  }, {});
}

export function containsForbiddenProfileFields(payload) {
  if (!payload || typeof payload !== 'object') return false;
  return PROFILE_FORBIDDEN_FIELDS.some((key) => key in payload);
}

function normalizeText(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Fields written on both first create and later sign-ins.
 * `accountType` upgrades anonymous -> google when the uid gains the provider.
 */
export function buildProfileFields(user, { displayName = null } = {}) {
  if (!user?.uid) return null;

  return sanitizeProfilePayload({
    uid: user.uid,
    displayName: (() => {
      const fromTyped = normalizeText(displayName);
      const fromAuth = normalizeText(authDisplayName(user));
      const chosen = [fromTyped, fromAuth].find((name) => name && !isGenericDisplayName(name));
      return chosen || null;
    })(),
    photoURL: normalizeText(user.photoURL),
    accountType: deriveAccountType(user) || ACCOUNT_TYPE.anonymous,
  });
}

export function buildProfileCreatePayload(user, { displayName = null, timestamp } = {}) {
  const fields = buildProfileFields(user, { displayName });
  if (!fields) return null;
  return { ...fields, createdAt: timestamp, updatedAt: timestamp };
}

/**
 * Update payload for an existing profile. `createdAt` is never rewritten, and
 * a null displayName/photoURL never clobbers a value already stored.
 */
export function buildProfileUpdatePayload(user, { displayName = null, timestamp, existing = null } = {}) {
  const fields = buildProfileFields(user, { displayName });
  if (!fields) return null;

  const payload = { uid: fields.uid, accountType: fields.accountType, updatedAt: timestamp };

  if (fields.displayName) {
    payload.displayName = fields.displayName;
  } else if (normalizeText(existing?.displayName)) {
    payload.displayName = normalizeText(existing.displayName);
  }

  if (fields.photoURL) {
    payload.photoURL = fields.photoURL;
  } else if (normalizeText(existing?.photoURL)) {
    payload.photoURL = normalizeText(existing.photoURL);
  }

  return sanitizeProfilePayload(payload);
}

/** Public-safe subset for participant UI. Never exposes email or provider data. */
export function toPublicParticipantProfile(profile) {
  if (!profile?.uid) return null;
  return {
    uid: profile.uid,
    displayName: normalizeText(profile.displayName),
    photoURL: normalizeText(profile.photoURL),
  };
}
